'use client'

import { useCallback, useRef, type ChangeEventHandler } from 'react'
import { useScreenshotBeautifierActions, useScreenshotBeautifierState, PRESETS } from './ScreenshotBeautifierContext'
import { Upload, RotateCcw, Plus, Minus } from 'lucide-react'
import { cn } from '~/lib/utils'

/**
 * Background gradient presets for quick selection
 */
const BACKGROUND_PRESETS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #8EC5FC 0%, #E0C3FC 100%)',
  'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  'linear-gradient(135deg, #fafafa 0%, #e5e5e5 100%)',
  'linear-gradient(135deg, #12001a 0%, #1a0028 100%)'
]

/**
 * Controls panel for the Screenshot Beautifier tool.
 * Provides comprehensive controls for all enhancement features including presets,
 * canvas settings, shadows, filters, and export options.
 *
 * @returns {JSX.Element} Controls panel component
 */
export const ScreenshotBeautifierControls = (): JSX.Element => {
  const { config } = useScreenshotBeautifierState()
  const { 
    setImage, 
    updateConfig, 
    applyPreset, 
    reset, 
    addShadow, 
    removeShadow, 
    updateShadow 
  } = useScreenshotBeautifierActions()
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  /**
   * Handles file upload and converts to data URL
   */
  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (event) => {
      if (event.target?.result) {
        setImage(event.target.result as string)
      }
    }
    reader.readAsDataURL(file)
  }, [setImage])

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-6">
        {/* Upload Section */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Upload</h3>
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4 hover:border-slate-900 hover:bg-slate-100 transition dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-sky-500 dark:hover:bg-slate-800">
            <input 
              ref={fileInputRef} 
              type="file" 
              accept="image/*" 
              onChange={handleFileUpload} 
              className="hidden" 
            />
            <Upload className="h-6 w-6 text-slate-500 dark:text-slate-400" />
            <div className="text-center">
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Click to upload</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-500">PNG, JPG, WebP</p>
            </div>
          </label>
        </div>

        {/* Presets Section */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Presets</h3>
          <div className="grid grid-cols-2 gap-2">
            {PRESETS.map(preset => (
              <button
                key={preset.id}
                onClick={() => applyPreset(preset)}
                className="rounded-lg border border-slate-300 bg-slate-50 p-2 text-left hover:border-slate-900 hover:bg-slate-100 transition dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-sky-500 dark:hover:bg-slate-800"
              >
                <div className="text-lg mb-1">{preset.emoji}</div>
                <div className="text-xs font-medium text-slate-700 dark:text-slate-300">{preset.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Canvas Section */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Canvas</h3>
          <div className="space-y-3">
            <div>
              <label className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                <span>Padding</span>
                <span className="text-slate-900 dark:text-sky-400">{config.padding}px</span>
              </label>
              <input
                type="range"
                min={0}
                max={200}
                value={config.padding}
                onChange={e => updateConfig({ padding: Number(e.target.value) })}
                className="w-full accent-slate-900 dark:accent-sky-500"
              />
            </div>
            <div>
              <label className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                <span>Radius</span>
                <span className="text-slate-900 dark:text-sky-400">{config.borderRadius}px</span>
              </label>
              <input
                type="range"
                min={0}
                max={80}
                value={config.borderRadius}
                onChange={e => updateConfig({ borderRadius: Number(e.target.value) })}
                className="w-full accent-slate-900 dark:accent-sky-500"
              />
            </div>
            <div>
              <label className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                <span>Scale</span>
                <span className="text-slate-900 dark:text-sky-400">{Math.round(config.scale * 100)}%</span>
              </label>
              <input
                type="range"
                min={50}
                max={150}
                value={config.scale * 100}
                onChange={e => updateConfig({ scale: Number(e.target.value) / 100 })}
                className="w-full accent-slate-900 dark:accent-sky-500"
              />
            </div>
          </div>
        </div>

        {/* Background Section */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Background</h3>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {BACKGROUND_PRESETS.map((gradient, i) => (
              <button
                key={i}
                onClick={() => updateConfig({ background: gradient })}
                className="h-10 rounded-lg border-2 border-slate-300 hover:border-slate-900 hover:scale-105 transition dark:border-slate-700 dark:hover:border-sky-500"
                style={{ background: gradient }}
              />
            ))}
          </div>
          <div>
            <label className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
              <span>Blur</span>
              <span className="text-slate-900 dark:text-sky-400">{config.backgroundBlur}px</span>
            </label>
            <input
              type="range"
              min={0}
              max={50}
              value={config.backgroundBlur}
              onChange={e => updateConfig({ backgroundBlur: Number(e.target.value) })}
              className="w-full accent-slate-900 dark:accent-sky-500"
            />
          </div>
        </div>

        {/* Shadows Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Shadows</h3>
            <button
              onClick={addShadow}
              className="p-1 rounded bg-slate-900 hover:bg-slate-700 transition dark:bg-sky-500 dark:hover:bg-sky-400"
            >
              <Plus className="h-3 w-3 text-white" />
            </button>
          </div>
          <div className="space-y-3">
            {config.shadows.map((shadow, idx) => (
              <div key={shadow.id} className="rounded-lg border border-slate-300 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Shadow {idx + 1}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateShadow(shadow.id, { enabled: !shadow.enabled })}
                      className={cn(
                        "text-xs px-2 py-1 rounded",
                        shadow.enabled 
                          ? 'bg-slate-900 text-white dark:bg-sky-500' 
                          : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                      )}
                    >
                      {shadow.enabled ? 'On' : 'Off'}
                    </button>
                    {config.shadows.length > 1 && (
                      <button 
                        onClick={() => removeShadow(shadow.id)} 
                        className="p-1 hover:bg-slate-200 rounded dark:hover:bg-slate-700"
                      >
                        <Minus className="h-3 w-3 text-slate-500 dark:text-slate-400" />
                      </button>
                    )}
                  </div>
                </div>
                {shadow.enabled && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-slate-500 dark:text-slate-500">X: {shadow.x}</label>
                        <input
                          type="range"
                          min={-100}
                          max={100}
                          value={shadow.x}
                          onChange={e => updateShadow(shadow.id, { x: Number(e.target.value) })}
                          className="w-full accent-slate-900 dark:accent-sky-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 dark:text-slate-500">Y: {shadow.y}</label>
                        <input
                          type="range"
                          min={-100}
                          max={100}
                          value={shadow.y}
                          onChange={e => updateShadow(shadow.id, { y: Number(e.target.value) })}
                          className="w-full accent-slate-900 dark:accent-sky-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 dark:text-slate-500">Blur: {shadow.blur}</label>
                        <input
                          type="range"
                          min={0}
                          max={150}
                          value={shadow.blur}
                          onChange={e => updateShadow(shadow.id, { blur: Number(e.target.value) })}
                          className="w-full accent-slate-900 dark:accent-sky-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 dark:text-slate-500">Spread: {shadow.spread}</label>
                        <input
                          type="range"
                          min={-100}
                          max={100}
                          value={shadow.spread}
                          onChange={e => updateShadow(shadow.id, { spread: Number(e.target.value) })}
                          className="w-full accent-slate-900 dark:accent-sky-500"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Noise Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Grain</h3>
            <button
              onClick={() => updateConfig({ noise: { ...config.noise, enabled: !config.noise.enabled }})}
              className={cn(
                "px-3 py-1 rounded text-xs font-semibold transition",
                config.noise.enabled 
                  ? 'bg-slate-900 text-white dark:bg-sky-500' 
                  : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
              )}
            >
              {config.noise.enabled ? 'On' : 'Off'}
            </button>
          </div>
          {config.noise.enabled && (
            <input
              type="range"
              min={0}
              max={50}
              value={config.noise.intensity}
              onChange={e => updateConfig({ noise: { ...config.noise, intensity: Number(e.target.value) }})}
              className="w-full accent-slate-900 dark:accent-sky-500"
            />
          )}
        </div>

        {/* 3D Tilt Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">3D Tilt</h3>
            <button
              onClick={() => updateConfig({ tilt: { ...config.tilt, enabled: !config.tilt.enabled }})}
              className={cn(
                "px-3 py-1 rounded text-xs font-semibold transition",
                config.tilt.enabled 
                  ? 'bg-slate-900 text-white dark:bg-sky-500' 
                  : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
              )}
            >
              {config.tilt.enabled ? 'On' : 'Off'}
            </button>
          </div>
          {config.tilt.enabled && (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400">X: {config.tilt.x}°</label>
                <input
                  type="range"
                  min={-30}
                  max={30}
                  value={config.tilt.x}
                  onChange={e => updateConfig({ tilt: { ...config.tilt, x: Number(e.target.value) }})}
                  className="w-full accent-slate-900 dark:accent-sky-500"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400">Y: {config.tilt.y}°</label>
                <input
                  type="range"
                  min={-30}
                  max={30}
                  value={config.tilt.y}
                  onChange={e => updateConfig({ tilt: { ...config.tilt, y: Number(e.target.value) }})}
                  className="w-full accent-slate-900 dark:accent-sky-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Adjustments</h3>
          <div className="space-y-2">
            {(['brightness', 'contrast', 'saturate'] as const).map(filter => (
              <div key={filter}>
                <label className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                  <span className="capitalize">{filter}</span>
                  <span className="text-slate-900 dark:text-sky-400">{config.filters[filter]}%</span>
                </label>
                <input
                  type="range"
                  min={filter === 'saturate' ? 0 : 50}
                  max={filter === 'saturate' ? 200 : 150}
                  value={config.filters[filter]}
                  onChange={e => updateConfig({ filters: { ...config.filters, [filter]: Number(e.target.value) }})}
                  className="w-full accent-slate-900 dark:accent-sky-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Border Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Border</h3>
            <button
              onClick={() => updateConfig({ border: { ...config.border, enabled: !config.border.enabled }})}
              className={cn(
                "px-3 py-1 rounded text-xs font-semibold transition",
                config.border.enabled 
                  ? 'bg-slate-900 text-white dark:bg-sky-500' 
                  : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
              )}
            >
              {config.border.enabled ? 'On' : 'Off'}
            </button>
          </div>
          {config.border.enabled && (
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400">Width: {config.border.width}px</label>
              <input
                type="range"
                min={1}
                max={20}
                value={config.border.width}
                onChange={e => updateConfig({ border: { ...config.border, width: Number(e.target.value) }})}
                className="w-full accent-slate-900 dark:accent-sky-500"
              />
            </div>
          )}
        </div>

        {/* Reset Button */}
        <button
          onClick={reset}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-slate-100 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 transition dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </button>
      </div>
    </div>
  )
}
